import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import type { Role } from '@keystone/shared';
import { avatarTint, initials, titleCase } from '../lib/format.js';
import { useAutoFocus, useCopy, useEscape } from '../lib/hooks.js';

/**
 * The component primitives.
 *
 * One file rather than twenty, because these are small, they share the same
 * token vocabulary, and the alternative is a reader opening twenty files to
 * learn one design system.
 *
 * ## The accessibility rules these encode
 *
 *   1. **Every control has a label.** `Field` generates the id, wires
 *      `htmlFor`, `aria-describedby` and `aria-invalid` together, and renders the
 *      error with a matching id. A page that forgets one of those four gets a
 *      control a screen reader announces as "edit text" with no name.
 *   2. **State is never carried by colour alone.** `Badge` always renders text;
 *      the tint is redundant. Around 8% of men have a form of colour blindness,
 *      and a red dot with no label is a red dot.
 *   3. **`type="button"` is the default.** A bare `<button>` inside a form
 *      submits it. Every button in this console is inside a form at some point,
 *      so the default is the safe one and `type="submit"` is opt-in.
 *   4. **Loading is announced.** `aria-busy` plus a visually hidden "Loading"
 *      means a spinner is not silent.
 *   5. **Dialogs trap attention.** `Modal` sets `aria-modal`, moves focus in,
 *      restores it on close, and closes on Escape.
 */

/* -------------------------------------------------------------------------- */
/*  Button                                                                    */
/* -------------------------------------------------------------------------- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-brand-ink hover:bg-brand-hover border border-transparent shadow-card',
  secondary:
    'bg-surface text-ink border border-line-strong hover:bg-surface-2 hover:border-line-strong',
  ghost: 'bg-transparent text-ink-muted border border-transparent hover:bg-surface-2 hover:text-ink',
  danger: 'bg-danger text-white border border-transparent hover:bg-danger-hover shadow-card',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-2.5 text-[0.8125rem] gap-1.5',
  md: 'h-9.5 px-3.5 text-sm gap-2',
};

/**
 * The class string for a button, exported so a `<Link>` can look like one.
 *
 * React Router's `Link` renders an `<a>`, and an `<a>` cannot be a `<button>`.
 * Rather than a polymorphic `as` prop — which TypeScript handles badly and which
 * produces an anchor with `disabled` on it — the shared class string lets the
 * caller pick the right element and keep the appearance.
 */
export function buttonClasses(variant: ButtonVariant = 'primary', size: ButtonSize = 'md'): string {
  return [
    'inline-flex items-center justify-center rounded-lg font-medium whitespace-nowrap',
    'transition-colors duration-150',
    'disabled:opacity-50 disabled:pointer-events-none',
    'aria-disabled:opacity-50 aria-disabled:pointer-events-none',
    VARIANTS[variant],
    SIZES[size],
  ].join(' ');
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  children,
  className = '',
  disabled,
  type = 'button',
  ...rest
}: ButtonProps): ReactNode {
  return (
    <button
      // Defaults to `button`. A bare `<button>` in a form submits it, and every
      // one of these ends up inside a form eventually.
      type={type}
      className={`${buttonClasses(variant, size)} ${className}`}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : icon}
      {children}
    </button>
  );
}

export function LinkButton({
  variant = 'secondary',
  size = 'md',
  className = '',
  children,
  ...rest
}: LinkProps & { variant?: ButtonVariant; size?: ButtonSize; children: ReactNode }): ReactNode {
  return (
    <Link className={`${buttonClasses(variant, size)} ${className}`} {...rest}>
      {children}
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/*  Spinner and skeletons                                                     */
/* -------------------------------------------------------------------------- */

export function Spinner({ size = 'md', label }: { size?: 'sm' | 'md' | 'lg'; label?: string }): ReactNode {
  const dimensions = size === 'sm' ? 'size-3.5' : size === 'lg' ? 'size-6' : 'size-4';
  return (
    <>
      <svg
        className={`${dimensions} animate-spin`}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
      {/* A spinner is silent to a screen reader without this. */}
      <span className="sr-only">{label ?? 'Loading'}</span>
    </>
  );
}

/** A placeholder block for content that is loading, sized by the caller. */
export function Skeleton({ className = '' }: { className?: string }): ReactNode {
  return <div className={`animate-pulse rounded-md bg-surface-3 ${className}`} aria-hidden="true" />;
}

/** Three shimmering rows, for a table that is still loading. */
export function SkeletonRows({ rows = 3, columns = 4 }: { rows?: number; columns?: number }): ReactNode {
  return (
    <>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <tr key={rowIndex}>
          {Array.from({ length: columns }, (_, columnIndex) => (
            <td key={columnIndex} className="px-4 py-3">
              <Skeleton className={columnIndex === 0 ? 'h-4 w-3/4' : 'h-4 w-1/2'} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/**
 * The fallback shown while a lazily-loaded page chunk is being fetched.
 *
 * A skeleton rather than a centred spinner, because this appears *inside* the
 * shell: the sidebar and header are already on screen, so replacing them with a
 * full-page splash would make the whole layout jump twice on every navigation.
 *
 * Lives here rather than in `App.tsx` because `Layout` needs it too, and a
 * component shared by the router and the shell cannot live in either without
 * creating an import cycle between them.
 */
export function PageFallback(): ReactNode {
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-4">
      <span className="sr-only">Loading page</span>
      <Skeleton className="h-7 w-56" />
      <Card className="p-4">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Fields                                                                    */
/* -------------------------------------------------------------------------- */

export interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /** Rendered as a suffix inside the control row, e.g. a "Generate" button. */
  addon?: ReactNode;
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
    required: boolean | undefined;
  }) => ReactNode;
}

/**
 * A labelled form control.
 *
 * Uses a render prop rather than accepting an element, so the generated id and
 * the ARIA wiring cannot be dropped. The alternative — a `Field` that wraps
 * children and hopes they accept an `id` — is how a form ends up with a label
 * pointing at nothing.
 */
export function Field({ label, hint, error, required, addon, children }: FieldProps): ReactNode {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  // Both the hint and the error are described, in that order, so a screen reader
  // reads "Password, at least 12 characters, error: ..." rather than losing the
  // hint when a validation message appears.
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[0.8125rem] font-medium text-ink">
        {label}
        {required ? (
          <span className="ml-1 text-danger" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      <div className={addon ? 'flex gap-2' : undefined}>
        {children({
          id,
          'aria-describedby': describedBy.length > 0 ? describedBy : undefined,
          'aria-invalid': error ? true : undefined,
          required: required ? true : undefined,
        })}
        {addon}
      </div>

      {hint && !error ? (
        <p id={hintId} className="text-xs text-ink-subtle">
          {hint}
        </p>
      ) : null}

      {error ? (
        // `role="alert"` so the message is announced when it appears, rather than
        // only being discoverable by navigating back to the field.
        <p id={errorId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const CONTROL =
  'w-full rounded-lg border border-line-strong bg-surface px-3 text-sm text-ink ' +
  'placeholder:text-ink-subtle transition-colors ' +
  'hover:border-line-strong disabled:opacity-50 ' +
  'aria-[invalid=true]:border-danger';

export function Input({ className = '', ...rest }: InputHTMLAttributes<HTMLInputElement>): ReactNode {
  return <input className={`${CONTROL} h-9.5 ${className}`} {...rest} />;
}

export function Textarea({
  className = '',
  rows = 4,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement>): ReactNode {
  return <textarea rows={rows} className={`${CONTROL} py-2 ${className}`} {...rest} />;
}

export function Select({
  className = '',
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement>): ReactNode {
  return (
    <select className={`${CONTROL} h-9.5 pr-8 ${className}`} {...rest}>
      {children}
    </select>
  );
}

/* -------------------------------------------------------------------------- */
/*  Surfaces                                                                  */
/* -------------------------------------------------------------------------- */

export function Card({
  children,
  className = '',
  as: Element = 'section',
}: {
  children: ReactNode;
  className?: string;
  as?: 'section' | 'div' | 'article';
}): ReactNode {
  return (
    <Element className={`rounded-xl border border-line bg-surface shadow-card ${className}`}>
      {children}
    </Element>
  );
}

export function CardHeader({
  title,
  description,
  actions,
  level = 2,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  /** Heading level, so a card inside a page keeps a sane outline. */
  level?: 2 | 3;
}): ReactNode {
  const Heading = level === 2 ? 'h2' : 'h3';
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3.5 sm:px-5">
      <div className="min-w-0">
        <Heading className="text-sm font-semibold text-ink">{title}</Heading>
        {description ? <p className="mt-0.5 text-xs text-ink-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Badges                                                                    */
/* -------------------------------------------------------------------------- */

type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-2 text-ink-muted border-line',
  brand: 'bg-brand-soft text-brand border-brand-line',
  success: 'bg-success-soft text-success border-success-line',
  warning: 'bg-warning-soft text-warning border-warning-line',
  danger: 'bg-danger-soft text-danger border-danger-line',
};

/**
 * A status pill.
 *
 * The tone is redundant with the text on purpose — every badge says what it is,
 * so the colour is a scanning aid rather than the message. The one place colour
 * carries meaning is the role badge, and even there the role name is spelled out.
 */
export function Badge({
  children,
  tone = 'neutral',
  className = '',
}: {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
}): ReactNode {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[0.6875rem] font-medium whitespace-nowrap ${TONES[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * A role, coloured by authority.
 *
 * `owner` gets the brand tint and `guest` gets nothing, because the ordering is
 * the only thing a reader scans a member list for. The word is always rendered.
 */
export function RoleBadge({ role }: { role: Role }): ReactNode {
  const tone: BadgeTone =
    role === 'owner' ? 'brand' : role === 'admin' ? 'success' : role === 'member' ? 'neutral' : 'warning';
  return <Badge tone={tone}>{titleCase(role)}</Badge>;
}

/* -------------------------------------------------------------------------- */
/*  Avatar                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * An avatar, falling back to coloured initials.
 *
 * The tint is derived from the identity, so the same person is the same colour
 * on every page — which is what makes the column scannable. A `alt` of the
 * person's name is set on the image and an `aria-hidden` wrapper carries the
 * initials, so a screen reader hears the name once either way.
 */
export function Avatar({
  name,
  src,
  size = 'md',
}: {
  name: string;
  src?: string | null;
  size?: 'sm' | 'md' | 'lg';
}): ReactNode {
  const dimensions = size === 'sm' ? 'size-7 text-[0.6875rem]' : size === 'lg' ? 'size-12 text-base' : 'size-9 text-xs';

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={`${dimensions} shrink-0 rounded-full border border-line object-cover`}
        loading="lazy"
        decoding="async"
      />
    );
  }

  return (
    <span
      className={`${dimensions} flex shrink-0 items-center justify-center rounded-full font-semibold text-white`}
      style={{ backgroundColor: avatarTint(name) }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  Feedback                                                                  */
/* -------------------------------------------------------------------------- */

type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const ALERT_TONES: Record<AlertTone, string> = {
  info: 'border-brand-line bg-brand-soft text-ink',
  success: 'border-success-line bg-success-soft text-ink',
  warning: 'border-warning-line bg-warning-soft text-ink',
  danger: 'border-danger-line bg-danger-soft text-ink',
};

/**
 * An inline message.
 *
 * `role="status"` for the calm tones and `role="alert"` for the alarming ones:
 * a success message should not interrupt whatever a screen reader is currently
 * reading, but a failure should. That distinction is the whole reason the two
 * roles exist.
 */
export function Alert({
  tone = 'info',
  title,
  children,
  actions,
}: {
  tone?: AlertTone;
  title?: string;
  children: ReactNode;
  actions?: ReactNode;
}): ReactNode {
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={`rounded-lg border px-3.5 py-3 text-sm ${ALERT_TONES[tone]}`}
    >
      {title ? <p className="font-medium">{title}</p> : null}
      <div className={title ? 'mt-0.5 text-[0.8125rem] text-ink-muted' : 'text-[0.8125rem]'}>
        {children}
      </div>
      {actions ? <div className="mt-2.5 flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <p className="text-sm font-medium text-ink">{title}</p>
      <p className="max-w-md text-[0.8125rem] text-ink-muted">{description}</p>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Modal                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * A modal dialog.
 *
 * Rendered inline rather than through a portal. A portal is the textbook answer
 * for escaping a stacking context, and the console has none to escape — but a
 * portal would also detach the dialog from its position in the DOM, which breaks
 * `aria-labelledby` for assistive technology that walks the tree rather than the
 * accessibility object model. Inline is both simpler and more correct here.
 *
 * The five things a dialog must do, all of which are easy to omit:
 *
 *   1. `role="dialog"` and `aria-modal="true"`
 *   2. a label, via `aria-labelledby`
 *   3. close on Escape
 *   4. move focus in on open, and put it back where it was on close
 *   5. keep Tab inside — `aria-modal` tells a screen reader to treat the rest of
 *      the page as inert, but the browser still lets Tab walk out of it
 *
 * Point 5 is why this component is longer than the rest. A dialog without a focus
 * trap is a dialog a keyboard user tabs out of and loses.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}): ReactNode {
  const titleId = useId();
  const descriptionId = `${titleId}-description`;
  const dialogRef = useRef<HTMLDivElement>(null);

  useEscape(open, onClose);

  useEffect(() => {
    if (!open) return;

    // Captured on the transition into `open`, not during render: reading
    // `document.activeElement` while rendering would capture the dialog itself.
    const restoreTo =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const frame = window.requestAnimationFrame(() => dialogRef.current?.focus());

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;

      const focusable = [
        ...node.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => element.offsetParent !== null);

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // A dialog with nothing focusable still must not let Tab escape.
      if (!first || !last) {
        event.preventDefault();
        return;
      }

      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === node)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);
      // Restores the keyboard user to the control they pressed, rather than to
      // the top of the document.
      restoreTo?.focus();
    };
  }, [open]);

  if (!open) return null;

  const width = size === 'sm' ? 'max-w-md' : size === 'lg' ? 'max-w-3xl' : 'max-w-xl';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center sm:p-6">
      {/*
        The backdrop is a button rather than a div with an onClick, so it is
        reachable and announced. `tabIndex={-1}` keeps it out of the tab order
        while remaining clickable.
      */}
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close dialog"
        onClick={onClose}
        className="fixed inset-0 cursor-default bg-[var(--k-overlay)]"
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(description ? { 'aria-describedby': descriptionId } : {})}
        tabIndex={-1}
        className={`relative z-10 w-full ${width} rounded-xl border border-line bg-surface shadow-pop`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-sm font-semibold text-ink">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="mt-0.5 text-xs text-ink-muted">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 -mt-1 rounded-lg p-1.5 text-ink-subtle transition-colors hover:bg-surface-2 hover:text-ink"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="px-5 py-4">{children}</div>

        {footer ? (
          <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3.5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A dialog that requires a typed confirmation before the destructive action.
 *
 * The same guard the API applies to organisation deletion, pushed to the point
 * of interaction. `confirmLabel` is what the caller must type — usually a slug or
 * a name — so the confirmation is specific to the thing being destroyed rather
 * than a reflex "OK".
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  confirmValue,
  confirmButton = 'Confirm',
  destructive = true,
  pending = false,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  /** When set, the caller must type this exact string to enable the button. */
  confirmValue?: string;
  confirmLabel?: string;
  confirmButton?: string;
  destructive?: boolean;
  pending?: boolean;
  error?: string;
}): ReactNode {
  const [typed, setTyped] = useState('');
  const inputRef = useAutoFocus<HTMLInputElement>(open && confirmValue !== undefined);

  // Reset on every open, so a cancelled dialog does not remember what was typed.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const satisfied = confirmValue === undefined || typed === confirmValue;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={!satisfied || pending}
            loading={pending}
          >
            {confirmButton}
          </Button>
        </>
      }
    >
      {error ? (
        <div className="mb-3">
          <Alert tone="danger">{error}</Alert>
        </div>
      ) : null}

      {confirmValue !== undefined ? (
        <Field
          label={confirmLabel ?? `Type "${confirmValue}" to confirm`}
          hint="This cannot be undone."
        >
          {(props) => (
            <input
              {...props}
              ref={inputRef}
              type="text"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              className="w-full rounded-lg border border-line-strong bg-surface px-3 font-mono text-sm text-ink"
              placeholder={confirmValue}
            />
          )}
        </Field>
      ) : null}
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/*  Table                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * A table wrapper.
 *
 * The horizontal scroll container is not decoration: the audit log has eight
 * columns and a metadata blob, and on a phone the alternative to scrolling is a
 * table that either overflows the viewport (breaking the page) or squashes a
 * column to two characters. `overflow-x-auto` with a `min-w` on the inner table
 * is the combination that does neither.
 *
 * The `<caption>` is visually hidden rather than absent. A table with no caption
 * is announced as an unlabelled grid, and every table here needs one.
 */
export function Table({
  caption,
  children,
  minWidth = 'min-w-[40rem]',
}: {
  caption: string;
  children: ReactNode;
  minWidth?: string;
}): ReactNode {
  return (
    <div className="overflow-x-auto">
      <table className={`w-full border-collapse text-left text-sm ${minWidth}`}>
        <caption className="sr-only">{caption}</caption>
        {children}
      </table>
    </div>
  );
}

export function Th({
  children,
  className = '',
  align = 'left',
}: {
  children: ReactNode;
  className?: string;
  align?: 'left' | 'right';
}): ReactNode {
  return (
    <th
      scope="col"
      className={`border-b border-line bg-surface-2 px-4 py-2.5 text-[0.6875rem] font-semibold tracking-wide text-ink-muted uppercase ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${className}`}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className = '',
  align = 'left',
}: {
  children: ReactNode;
  className?: string;
  align?: 'left' | 'right';
}): ReactNode {
  return (
    <td
      className={`border-b border-line px-4 py-3 align-middle text-ink ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${className}`}
    >
      {children}
    </td>
  );
}

/* -------------------------------------------------------------------------- */
/*  Pagination                                                                */
/* -------------------------------------------------------------------------- */

/**
 * A cursor-pagination control.
 *
 * There is no "page 3 of 12" and no total count, because the API does not
 * compute one — a `COUNT(*)` over a filtered audit table is a full scan on every
 * page load, and the number is not worth that. So the control offers "Load more"
 * and, once the reader has paged, a way back to the start. It keeps the pages
 * already fetched in state rather than discarding them, so "back" is instant and
 * does not re-query.
 */
export function Pagination({
  hasMore,
  loading,
  onLoadMore,
  onReset,
  loaded,
}: {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  onReset: () => void;
  /** How many rows are currently on screen, for the summary line. */
  loaded: number;
}): ReactNode {
  if (!hasMore && loaded === 0) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
      <p className="text-xs text-ink-muted" aria-live="polite">
        Showing {loaded} {loaded === 1 ? 'entry' : 'entries'}
        {hasMore ? '' : ' — that is all of them'}
      </p>

      <div className="flex items-center gap-2">
        {loaded > 0 && !hasMore ? (
          <Button variant="ghost" size="sm" onClick={onReset}>
            Back to start
          </Button>
        ) : null}
        {hasMore ? (
          <Button variant="secondary" size="sm" onClick={onLoadMore} loading={loading}>
            Load more
          </Button>
        ) : null}
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Icons                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Inline SVGs rather than an icon dependency.
 *
 * Two icons are needed. An icon package would add a dependency, a tree-shaking
 * configuration and a licence to review in exchange for saving twenty lines —
 * and the icons would then be the one part of the design system not written in
 * the token vocabulary. `currentColor` and `aria-hidden` are the two attributes
 * that matter, and both are easy to state here.
 */
export function CloseIcon(): ReactNode {
  return (
    <svg viewBox="0 0 20 20" className="size-4" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M5.5 5.5l9 9m0-9l-9 9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function ChevronIcon({ direction = 'down' }: { direction?: 'down' | 'right' }): ReactNode {
  return (
    <svg
      viewBox="0 0 20 20"
      className={`size-4 transition-transform duration-150 ${direction === 'right' ? '-rotate-90' : ''}`}
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5.5 8l4.5 4.5L14.5 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/*  Copy                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A copy-to-clipboard control.
 *
 * Falls back to a selectable read-only input when the Clipboard API is
 * unavailable — which happens on an insecure origin, and in a document that is
 * not focused. Silently doing nothing is the worst outcome, so the value is
 * always on screen and selectable.
 */
export function CopyButton({
  value,
  label = 'Copy',
  size = 'sm',
}: {
  value: string;
  label?: string;
  size?: ButtonSize;
}): ReactNode {
  const { copied, failed, copy } = useCopy();

  return (
    <Button
      variant="ghost"
      size={size}
      onClick={() => void copy(value)}
      aria-label={`${label}: ${value}`}
    >
      {copied ? 'Copied' : failed ? 'Select to copy' : label}
    </Button>
  );
}
